import { useQueryClient } from "@tanstack/react-query";
import { FC } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import { getGetVolumesQueryKey } from "~/api/folders/folders";
import { useDeleteProject, useGetProject } from "~/api/projects/projects";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { ROUTES, useProjectParams } from "~/helpers";
import { trackEvent } from "~/utils/analytics";
import { useParentCase } from "../hooks/useParentCase.ts";

interface DeleteVersionDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  projectId: string;
}

export const DeleteVersionDialog: FC<DeleteVersionDialogProps> = ({
  open,
  setOpen,
  projectId,
}) => {
  const { organizationId, caseId } = useProjectParams();
  const { project: parentCase } = useParentCase();
  const { mutateAsync: deleteProject } = useDeleteProject();
  const { data: project } = useGetProject(organizationId, caseId);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const handleDelete = async () => {
    trackEvent({
      eventName: "app/version_delete_modal/delete.button/submit",
    });

    await deleteProject({
      organizationId,
      projectId,
    });

    if (!parentCase?.id) return;

    navigate(ROUTES.CASE({ organizationId, caseId: parentCase.id }));

    queryClient.invalidateQueries({
      queryKey: getGetVolumesQueryKey(organizationId, parentCase.id),
    });

    setOpen(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <FormattedMessage
              id="deleteVersion"
              defaultMessage="Delete Version"
            />
          </AlertDialogTitle>
          <AlertDialogDescription>
            <FormattedMessage
              id="deleteVersionDescription"
              defaultMessage="Are you sure you want to delete {versionName} version? All changes associated with this version will be removed including data from Launch Sky."
              values={{
                versionName: <strong>{project?.name || "this"}</strong>,
              }}
            />
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={() => {
              trackEvent({
                eventName: "app/version_delete_modal/cancel.button/click",
              });
            }}
          >
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            className="bg-red-600 hover:bg-red-700"
          >
            <FormattedMessage id="delete" defaultMessage="Delete" />
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
